import { useState, useMemo, useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { State } from '../state-actions-reducers'
import { useFormStore } from '../store/formStore'
import { useDefer } from '../utils/helpers'
import { validateStep } from '../validate/validators'

interface UseFormValidationArgs {
  methods: UseFormReturn<State>
  formChangeCount: number
  currentStep: number
  imageError: any[]
}

interface UseFormValidationResult {
  nextStep: boolean
  setNextStep: (v: boolean) => void
  invalidFields: string[]
}

/**
 * Manages step-by-step validation:
 * - Runs validateStep when "Next" is triggered
 * - Debounced re-validation on every field change (while strictValidation is active)
 * - Surgically updates RHF errors to avoid flash renders
 */
export function useFormValidation({
  methods,
  formChangeCount,
  currentStep,
  imageError,
}: UseFormValidationArgs): UseFormValidationResult {
  const { strictValidation, setStrictValidation, setCurrentStep } = useFormStore()
  const [nextStep, setNextStep] = useState(false)

  const invalidFields = useMemo(
    () => Object.keys(methods.formState.errors),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods.formState.errors]
  )

  function applyValidationErrors(invalidFieldNames: string[]) {
    const current = Object.keys(methods.formState.errors)
    current.forEach(field => {
      if (!invalidFieldNames.includes(field)) methods.clearErrors(field as any)
    })
    invalidFieldNames.forEach(field => {
      methods.setError(field as any, { type: 'required', message: '' })
    })
  }

  // Triggered when user clicks "Next"
  useEffect(() => {
    if (!nextStep) return
    setNextStep(false)
    setStrictValidation(true)

    const snapshot = { ...methods.getValues(), currentStep } as any
    const errors = validateStep(currentStep, snapshot)

    if (errors.length === 0 && imageError.length === 0) {
      methods.clearErrors()
      setStrictValidation(false)
      setCurrentStep(currentStep + 1)
      window.scrollTo(0, 0)
      return
    }
    applyValidationErrors(errors)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextStep])

  // Re-validate on every field change while strictValidation is active (300 ms debounce)
  useDefer(
    () => {
      if (!strictValidation) return
      const snapshot = { ...methods.getValues(), currentStep } as any
      applyValidationErrors(validateStep(currentStep, snapshot))
    },
    [formChangeCount, strictValidation, currentStep],
    300
  )

  return { nextStep, setNextStep, invalidFields }
}
